import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  getJobPosts,
  deleteJobPost,
  JobPost,
} from "../../../../app/helper/jobService";
import { ReactComponent as JobPostIcon } from "../../../../app/assets/images/job-post.svg";
import DynamicSVG from "../../../atoms/DynamicSvg/DynamicSvg";

const JobPostList: React.FC = () => {
  const [jobs, setJobs] = useState<JobPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [showConfirmation, setShowConfirmation] = useState<string | null>(null);

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const fetchedJobs = await getJobPosts();
      setJobs(fetchedJobs);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An error occurred fetching jobs"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteJob = async (jobId: string) => {
    try {
      setDeleting(jobId);
      const success = await deleteJobPost(jobId);

      if (success) {
        setJobs((prev) => prev.filter((job) => job.id !== jobId));
      } else {
        setError("Failed to delete job post");
      }
    } catch (err) {
      console.error("Error deleting job:", err);
      setError(
        err instanceof Error
          ? err.message
          : "An error occurred deleting the job"
      );
    } finally {
      setDeleting(null);
      setShowConfirmation(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
        role="alert"
      >
        <strong className="font-bold">Error!</strong>
        <span className="block sm:inline"> {error}</span>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {jobs.map((job) => {
        if (!job.id) return null;

        return (
          <div
            key={job.id}
            className="bg-blackDarkTheme rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300 relative"
          >
            <div className="h-20 w-20 mx-auto mt-5">
              <DynamicSVG
                color="#fff"
                size="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
              >
                <JobPostIcon />
              </DynamicSVG>
            </div>
            <div className="p-4">
              <h3 className="text-xl font-semibold text-white mb-2 line-clamp-1">
                {job.title}
              </h3>
              <p className="text-gray-300 text-sm mb-4 line-clamp-3">
                {job.description}
              </p>
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="px-2 py-1 text-xs font-medium bg-yellow text-black rounded-full">
                  Experience: {job.experience}
                </span>
                <span className="px-2 py-1 text-xs font-medium orange-bg-600 text-white rounded-full">
                  Vacancies: {job.vacancy}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <Link
                  to={`/edit-job/${job.id}`}
                  className="text-indigo-400 hover:text-indigo-300 text-sm font-medium"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 0L19.5 7.586m-2.828-2.828L3.586 17.172a2 2 0 00-.586 1.414v1.828a2 2 0 002 2h1.828a2 2 0 001.414-.586L21.328 8.742"
                    />
                  </svg>
                </Link>
                <button
                  onClick={() => job.id && setShowConfirmation(job.id)}
                  className="text-red-400 hover:text-red-300 text-sm font-medium"
                  disabled={deleting === job.id}
                >
                  {deleting === job.id ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>

            {/* Delete Confirmation Modal */}
            {showConfirmation === job.id && (
              <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
                <div className="bg-blackDarkTheme rounded-lg p-6 max-w-sm w-full">
                  <h3 className="text-lg font-medium text-white mb-4">
                    Confirm Delete
                  </h3>
                  <p className="text-gray-300 mb-6">
                    Are you sure you want to delete this job post? This action
                    cannot be undone.
                  </p>
                  <div className="flex justify-end space-x-4">
                    <button
                      onClick={() => setShowConfirmation(null)}
                      className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => job.id && handleDeleteJob(job.id)}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
                      disabled={deleting === job.id}
                    >
                      {deleting === job.id ? "Deleting..." : "Delete"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default JobPostList;
